import { Appearance, loadStripe, Stripe, StripeElements } from "@stripe/stripe-js";
import { amountToMinorUnits, getOrderStore } from "../utils";
import Alpine from "alpinejs";
import { AmountDto } from "../api";

export class StripeStore {
    private pk: string;
    private appearance: Appearance | undefined;
    private knownTransactionAmount: AmountDto | null = null;
    private stripeElements?: StripeElements | null = null;
    private stripeState: "not_ready" | "initializing" | "ready" = "not_ready";

    public stripe: Stripe | null = null;
    public stripeReady: boolean = false;
    public allowedCountries: string[] | undefined;

    constructor(pk: string, appearance?: Appearance, allowedCountries?: string[]) {
        this.pk = pk;
        this.appearance = appearance;
        this.allowedCountries = allowedCountries?.map(c => c.toUpperCase());
    }

    init() {
        console.debug("StripeStore initialized");

        if(!this.pk) {
            console.error("Stripe publishable key is not defined in StripeStore");
        }
        this.initializeStripe(this.pk);

        Alpine.effect(() => {
            this.updateTransactionAmount(getOrderStore().transactionAmount);
        });
    }


    /**
     * Gets Stripe Elements instance configured for 'payment' mode with the specified or current order amount.
     *
     * @param transactionAmount - Optional formatted amount for the transaction. If provided, creates a new Elements instance for this specific amount.
     * @param currencyCode - Optional currency code for the transaction. Required when transactionAmount is provided.
     * @returns A configured StripeElements instance for payment processing, or null if Stripe is not ready or required data is missing.
     *
     * @remarks
     * - If both transactionAmount and currencyCode are provided, returns a new Elements instance for that specific amount
     * - If no parameters are provided, returns the cached Elements instance for the current order
     * - If Stripe is not ready, logs a warning and returns null
     * - Currency code is automatically converted to lowercase
     * - Amount is converted to minor units (e.g., cents for USD)
     */
    public getStripeElements(): StripeElements | null | undefined {
        if(!this.stripeReady) {
            console.warn("Stripe is not ready yet");
            return;
        }

         if(!this.stripeElements) {
            // Otherwise return the stored instance for the current order.
            const orderStore = getOrderStore();
            const transactionAmount = orderStore.transactionAmount;
            if(!transactionAmount || transactionAmount.value === 0) {
                return;
            }

            this.stripeElements = this.stripe!.elements({
                mode: "payment",
                currency: orderStore.currencyCode.toLowerCase(),
                amount: amountToMinorUnits(transactionAmount.value),
                fonts: [
                    { cssSrc: 'https://use.typekit.net/upe0mue.css' }
                ],
                appearance: {
                    ...this.appearance
                }
            });
        }

        return this.stripeElements;
    }

    private async initializeStripe(pk: string) {
        if(this.stripeState === "initializing" || this.stripeReady) {
            return;
        }
        this.stripeState = "initializing";
        if (!this.stripe && this.stripeReady === false) {
            this.stripe = await loadStripe(pk);
            this.stripeReady = true;
            this.stripeState = "ready";
        }

        if(!this.stripeElements) {
            const orderStore = getOrderStore();
            if(orderStore.transactionAmount == null || orderStore.transactionAmount.value === 0) {
                return;
            }
            this.stripeElements = this.stripe!.elements({
                mode: "payment",
                currency: orderStore.currencyCode.toLowerCase(),
                amount: amountToMinorUnits(orderStore.transactionAmount?.value ?? 0),
                fonts: [
                    { cssSrc: 'https://use.typekit.net/upe0mue.css' }
                ],
                appearance: this.appearance,
            });
        }
    }

    public updateTransactionAmount(transactionAmount: AmountDto | null | undefined) {
        if(transactionAmount == null || transactionAmount == undefined || transactionAmount.value === 0)
            return;

        if(!this.stripeReady) {
            this.initializeStripe(this.pk);
        }

        if(!this.stripeElements) {
            if(this.stripe == null) {
                return;
            }
            const orderStore = getOrderStore();
            this.stripeElements = this.stripe!.elements({
                mode: "payment",
                currency: orderStore.currencyCode.toLowerCase(),
                amount: amountToMinorUnits(transactionAmount.value),
                fonts: [
                    { cssSrc: 'https://use.typekit.net/upe0mue.css' }
                ],
                appearance: {
                    ...this.appearance
                }
            });
            this.knownTransactionAmount = transactionAmount;
            return;
        } else {
            this.getStripeElements();
        }

        if(this.knownTransactionAmount?.value !== transactionAmount.value) {
            console.debug(`Updating Stripe Elements Transaction amount to ${transactionAmount.value}`);
            this.stripeElements!.update({
                amount: amountToMinorUnits(transactionAmount?.value),
            });
        }

        this.knownTransactionAmount = transactionAmount;
    }
}